/** One function per API endpoint, bound to an `ApiClient`. Paths mirror `apps/api/moment_api/routers/*.py`. */
import { ApiError, type ApiClient } from './client';
import type {
  PerformanceCampaignsOut,
  PerformanceImportOut,
  PerformanceImportRequest,
  PerformanceSummaryOut,
  PerformanceSummaryParams,
} from './types';
import type {
  AnalyzeRequest,
  ApiKeyCreate,
  ApiKeyCreated,
  ApiKeyOut,
  BulkAnalyzeOut,
  BulkAnalyzeRequest,
  CampaignCreate,
  CampaignOut,
  CampaignPatch,
  CampaignStats,
  CampaignStatus,
  CostsOut,
  CreativeCreate,
  CreativeOut,
  CreativePreviewOut,
  CreativeTemplates,
  CreativeUpdate,
  EstimateOut,
  ExportCreate,
  ExportOut,
  JobListParams,
  JobOut,
  MomentOut,
  MomentSearchOut,
  MomentSearchParams,
  Page,
  ProspectsOut,
  ProspectsRequest,
  PublisherCreate,
  PublisherLookupOut,
  PublisherOut,
  PublisherUpdate,
  PublisherVideoCreate,
  PublisherVideoOut,
  SegmentCreate,
  SegmentMemberOut,
  SegmentOut,
  SegmentPreviewOut,
  SegmentPreviewRequest,
  SegmentStatus,
  SegmentUpdate,
  SourceCreate,
  SourceOut,
  UploadOut,
  UploadUrlOut,
  VideoDetail,
  VideoListParams,
  VideoOut,
  WebhookSecretOut,
} from './types';

/** Multipart POST with upload progress; `fetch` has no progress events, so this one call uses XMLHttpRequest. */
function uploadFile(c: ApiClient, path: string, file: File, onProgress?: (fraction: number) => void, signal?: AbortSignal) {
  return new Promise<UploadOut>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', c.url(path));
    xhr.withCredentials = true;
    for (const [k, v] of Object.entries(c.headers())) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onerror = () => reject(new ApiError(0, 'Network error: could not reach the Moment API'));
    xhr.onabort = () => reject(new DOMException('Upload aborted', 'AbortError'));
    xhr.onload = () => {
      let data: unknown = null;
      try {
        data = JSON.parse(xhr.responseText) as unknown;
      } catch {
        data = xhr.responseText;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(data as UploadOut);
        return;
      }
      const detail = data && typeof data === 'object' && 'detail' in data ? (data as { detail: unknown }).detail : data;
      reject(new ApiError(xhr.status, detail));
    };
    signal?.addEventListener('abort', () => xhr.abort());
    const form = new FormData();
    form.append('file', file, file.name);
    xhr.send(form);
  });
}

export function createEndpoints(c: ApiClient) {
  return {
    apiKeys: {
      list: () => c.get<ApiKeyOut[]>('/api-keys'),
      create: (body: ApiKeyCreate) => c.post<ApiKeyCreated>('/api-keys', body),
      revoke: (id: string) => c.delete(`/api-keys/${id}`),
    },
    sources: {
      list: (cursor?: string | null, limit = 50) => c.get<Page<SourceOut>>('/sources', { cursor, limit }),
      get: (id: string) => c.get<SourceOut>(`/sources/${id}`),
      create: (body: SourceCreate) => c.post<SourceOut>('/sources', body),
      discover: (id: string) => c.post<SourceOut>(`/sources/${id}/discover`),
      estimate: (id: string, params: { tier?: string; fps?: number | null; only_unanalyzed?: boolean }) =>
        c.get<EstimateOut>(`/sources/${id}/estimate`, params),
      delete: (id: string) => c.delete(`/sources/${id}`),
    },
    uploads: {
      /** Stores one video file; the result's `url` goes into a url_list source, its `key` into `config.upload_keys`. */
      create: (file: File, onProgress?: (fraction: number) => void, signal?: AbortSignal) =>
        uploadFile(c, '/uploads', file, onProgress, signal),
      /** A fresh URL for a stored upload (the one returned at upload time is valid for 7 days). */
      url: (key: string) => c.get<UploadUrlOut>(`/uploads/${key.split('/').map(encodeURIComponent).join('/')}/url`),
    },
    videos: {
      list: (params: VideoListParams & { cursor?: string | null }) =>
        c.get<Page<VideoOut>>('/videos', { ...params, limit: params.limit ?? 50 }),
      get: (id: string) => c.get<VideoDetail>(`/videos/${id}`),
      analyze: (id: string, body?: AnalyzeRequest) => c.post<VideoOut>(`/videos/${id}/analyze`, body ?? {}),
      reanalyze: (id: string, body?: AnalyzeRequest) => c.post<VideoOut>(`/videos/${id}/reanalyze`, body ?? {}),
      bulkAnalyze: (body: BulkAnalyzeRequest) => c.post<BulkAnalyzeOut>('/videos/bulk-analyze', body),
      delete: (id: string) => c.delete(`/videos/${id}`),
    },
    moments: {
      search: (params: MomentSearchParams, signal?: AbortSignal) =>
        c.get<MomentSearchOut>('/moments/search', { ...params, limit: params.limit ?? 48 }, signal),
      get: (id: string) => c.get<MomentOut>(`/moments/${id}`),
    },
    segments: {
      list: (params?: { status?: SegmentStatus; cursor?: string | null; limit?: number }) =>
        c.get<Page<SegmentOut>>('/segments', { ...params, limit: params?.limit ?? 100 }),
      get: (id: string) => c.get<SegmentOut>(`/segments/${id}`),
      create: (body: SegmentCreate) => c.post<SegmentOut>('/segments', body),
      update: (id: string, body: SegmentUpdate) => c.put<SegmentOut>(`/segments/${id}`, body),
      delete: (id: string) => c.delete(`/segments/${id}`),
      recompute: (id: string) => c.post<SegmentOut>(`/segments/${id}/recompute`),
      preview: (body: SegmentPreviewRequest, signal?: AbortSignal) =>
        c.request<SegmentPreviewOut>('POST', '/segments/preview', { body, signal }),
      members: (id: string, offset = 0, limit = 100) =>
        c.get<Page<SegmentMemberOut>>(`/segments/${id}/members`, { offset, limit }),
      webhookSecret: (id: string) => c.get<WebhookSecretOut>(`/segments/${id}/webhook-secret`),
    },
    exports: {
      list: (params?: { segment_id?: string; cursor?: string | null; limit?: number }) =>
        c.get<Page<ExportOut>>('/exports', { ...params, limit: params?.limit ?? 50 }),
      get: (id: string) => c.get<ExportOut>(`/exports/${id}`),
      create: (body: ExportCreate) => c.post<ExportOut>('/exports', body),
      downloadUrl: (id: string) => c.url(`/exports/${id}/download`),
      downloadBlob: (id: string) => c.fetchBlobUrl(`/exports/${id}/download`),
    },
    publishers: {
      list: () => c.get<Page<PublisherOut>>('/publishers'),
      get: (id: string) => c.get<PublisherOut>(`/publishers/${id}`),
      create: (body: PublisherCreate) => c.post<PublisherOut>('/publishers', body),
      update: (id: string, body: PublisherUpdate) => c.patch<PublisherOut>(`/publishers/${id}`, body),
      rotateKey: (id: string) => c.post<PublisherOut>(`/publishers/${id}/rotate-key`),
      videos: (id: string, offset = 0, limit = 200) =>
        c.get<Page<PublisherVideoOut>>(`/publishers/${id}/videos`, { offset, limit }),
      mapVideo: (id: string, body: PublisherVideoCreate) =>
        c.post<PublisherVideoOut>(`/publishers/${id}/videos`, body),
      unmapVideo: (id: string, mappingId: string) => c.delete(`/publishers/${id}/videos/${mappingId}`),
      lookup: (domain: string, signal?: AbortSignal) =>
        c.get<PublisherLookupOut>('/publishers/lookup', { domain }, signal),
      prospects: (body: ProspectsRequest, signal?: AbortSignal) =>
        c.request<ProspectsOut>('POST', '/publishers/prospects', { body, signal }),
    },
    creatives: {
      templates: () => c.get<CreativeTemplates>('/creatives/templates'),
      list: () => c.get<Page<CreativeOut>>('/creatives'),
      get: (id: string) => c.get<CreativeOut>(`/creatives/${id}`),
      create: (body: CreativeCreate) => c.post<CreativeOut>('/creatives', body),
      update: (id: string, body: CreativeUpdate) => c.patch<CreativeOut>(`/creatives/${id}`, body),
      delete: (id: string) => c.delete(`/creatives/${id}`),
      preview: (body: CreativeCreate, signal?: AbortSignal) =>
        c.request<CreativePreviewOut>('POST', '/creatives/preview', { body, signal }),
      previewSaved: (id: string, body?: CreativeUpdate) =>
        c.post<CreativePreviewOut>(`/creatives/${id}/preview`, body ?? {}),
    },
    campaigns: {
      list: (status?: CampaignStatus) => c.get<Page<CampaignOut>>('/overlay-campaigns', { status }),
      get: (id: string) => c.get<CampaignOut>(`/overlay-campaigns/${id}`),
      create: (body: CampaignCreate) => c.post<CampaignOut>('/overlay-campaigns', body),
      patch: (id: string, body: CampaignPatch) => c.patch<CampaignOut>(`/overlay-campaigns/${id}`, body),
      delete: (id: string) => c.delete(`/overlay-campaigns/${id}`),
      stats: (id: string, days = 30) => c.get<CampaignStats>(`/overlay-campaigns/${id}/stats`, { days }),
    },
    costs: {
      get: (granularity: 'day' | 'month', days: number) => c.get<CostsOut>('/costs', { granularity, days }),
    },
    jobs: {
      list: (params?: JobListParams & { cursor?: string | null }) =>
        c.get<Page<JobOut>>('/jobs', { ...params, limit: params?.limit ?? 50 }),
      get: (id: string) => c.get<JobOut>(`/jobs/${id}`),
    },
    performance: {
      /** Multipart upload of a DV360 or Google Ads placement report; `ApiClient.request` only sends JSON. */
      importReport: async (body: PerformanceImportRequest): Promise<PerformanceImportOut> => {
        const fd = new FormData();
        fd.append('file', body.file, body.file.name);
        fd.append('kind', body.kind);
        for (const k of ['campaign_ref', 'segment_id', 'export_id', 'date_from', 'date_to'] as const) {
          const v = body[k];
          if (v) fd.append(k, v);
        }
        let res: Response;
        try {
          res = await fetch(c.url('/performance/import'), {
            method: 'POST',
            headers: c.headers(),
            body: fd,
            credentials: 'include',
          });
        } catch {
          throw new ApiError(0, 'Network error: could not reach the Moment API');
        }
        const text = await res.text();
        let data: unknown = null;
        try {
          data = text ? JSON.parse(text) : null;
        } catch {
          data = text;
        }
        if (!res.ok) {
          const detail =
            data && typeof data === 'object' && 'detail' in data ? (data as { detail: unknown }).detail : data;
          throw new ApiError(res.status, detail);
        }
        return data as PerformanceImportOut;
      },
      summary: (params: PerformanceSummaryParams) =>
        c.get<PerformanceSummaryOut>('/performance/summary', { ...params }),
      campaigns: () => c.get<PerformanceCampaignsOut>('/performance/campaigns'),
      /** Drops every imported row of one campaign (wrong file, test import); a re-import recreates them. */
      deleteCampaign: (campaignRef: string) =>
        c.delete<void>(`/performance/campaigns?campaign_ref=${encodeURIComponent(campaignRef)}`),
    },
  };
}

export type Endpoints = ReturnType<typeof createEndpoints>;
